import { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { cn } from '@/lib/utils';
import { MenuBar } from './menu-bar';
import { CustomTextAlign, CustomTypography, CustomImage, CustomLink, CustomTable, CustomTableRow, CustomTableCell, CustomTableHeader } from './extensions';

interface SimpleEditorProps {
    content?: string;
    contentJSON?: any;
    onChange: (html: string, json: any) => void;
    className?: string;
}

export const SimpleEditor = ({ content, contentJSON, onChange, className }: SimpleEditorProps) => {
    const editor = useEditor({
        extensions: [
            StarterKit,
            CustomTypography,
            CustomTextAlign.configure({
                types: ['heading', 'paragraph'],
            }),
            CustomImage,
            CustomLink,
            CustomTable,
            CustomTableRow,
            CustomTableCell,
            CustomTableHeader,
        ],
        content: contentJSON || content || '',
        onUpdate: ({ editor }) => {
            onChange(editor.getHTML(), editor.getJSON());
        },
        editorProps: {
            attributes: {
                class: 'prose prose-sm dark:prose-invert max-w-none focus:outline-none h-full min-h-full p-6 focus:ring-0 focus-visible:ring-0 ring-0 ring-offset-0',
            },
        },
    });

    // Update content if it changes externally (e.g. on reset or fetch)
    useEffect(() => {
        if (!editor) return;

        const currentHTML = editor.getHTML();
        const currentJSON = JSON.stringify(editor.getJSON());

        if (contentJSON) {
            if (JSON.stringify(contentJSON) !== currentJSON) {
                editor.commands.setContent(contentJSON);
            }
        } else if (content !== undefined && content !== currentHTML) {
            editor.commands.setContent(content);
        }
    }, [content, contentJSON, editor]);

    return (
        <div className={cn("border rounded-md overflow-hidden bg-background flex flex-col h-[450px] shadow-sm", className)}>
            <MenuBar editor={editor} />
            <div
                className="flex-1 overflow-y-auto custom-scrollbar cursor-text bg-white"
                onClick={() => editor?.commands.focus()}
            >
                <EditorContent editor={editor} />
            </div>
        </div>
    );
};
